/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — DataTable body row.
 *
 * Split out and memoised so the body render loop stays the single place that
 * decides which rows exist. Row virtualisation can be introduced there later
 * without touching the public API.
 */
import cx from 'classnames';
import * as React from 'react';

import type { DataTableColumn } from '../../types';
import { Checkbox } from '../Checkbox';
import { Radio } from '../Radio';
import { isBlankCellValue, readCellValue, toDisplayText } from './resolveCellValue';
import type { PinnedOffsets } from './usePinnedColumns';
import { pinnedCellClassNames, pinnedCellStyle, SELECTION_COLUMN_KEY } from './usePinnedColumns';

interface DataTableRowProps<TData> {
  row: TData;
  rowIndex: number;
  rowKey: string;
  columns: DataTableColumn<TData>[];
  emptyValue: React.ReactNode;
  /** `false` renders no selection cell; otherwise checkbox or radio. */
  selectionMode: 'single' | 'multiple' | false;
  selected: boolean;
  selectionDisabled: boolean;
  selectionLabel: string;
  /** Shared radio-group name, so single-select rows form one group. */
  selectionName?: string;
  /** `shiftKey` drives range selection, which only applies to multiple mode. */
  onToggleSelect?: (key: string, options: { shiftKey: boolean }) => void;
  onRowClick?: (row: TData, rowIndex: number) => void;
  /** Sticky offsets per column key, empty when nothing is pinned. */
  pinnedOffsets: PinnedOffsets;
  className?: string;
}

/**
 * Warn once per column, so a table re-rendering on a poll cannot fill the
 * console with the same message.
 */
const warnedColumns = new Set<string>();

/** Test-only: lets a spec assert the warning without depending on test order. */
export const resetCellValueWarnings = () => warnedColumns.clear();

/**
 * Flags a column that received a value React cannot render. The value is shown
 * as text rather than dropped, so the cell degrades instead of the page
 * crashing — but the column really wants a `render`.
 */
const warnUnrenderableValue = (columnKey: string, value: unknown) => {
  if (process.env.NODE_ENV === 'production') return;
  if (warnedColumns.has(columnKey)) return;
  warnedColumns.add(columnKey);

  // eslint-disable-next-line no-console
  console.warn(
    `[DataTable] Column "${columnKey}" received a ${
      Array.isArray(value) ? 'array' : typeof value
    } value, which React cannot render directly. It has been stringified so the ` +
      'table still renders. Give the column a `render` to control how it appears, ' +
      `e.g. { key: '${columnKey}', render: (row, i, value) => … }.`
  );
};

interface CellContent {
  node: React.ReactNode;
  /** Plain text of the cell, when it has any — used for the overflow tooltip. */
  text?: string;
  /** The resolved value, so the tooltip does not have to read the row again. */
  value: unknown;
}

/**
 * Resolves a cell's content, and the plain text behind it where there is any.
 *
 * A column's `render` wins outright. Otherwise the value is coerced to something
 * renderable: primitives pass through, and objects, arrays and dates are
 * stringified rather than handed to React, which would throw and unmount the
 * whole screen.
 */
const resolveCellContent = <TData,>(
  column: DataTableColumn<TData>,
  row: TData,
  rowIndex: number,
  emptyValue: React.ReactNode
): CellContent => {
  const value = readCellValue(row, column.key, column.field);

  if (column.render) {
    return { node: column.render(row, rowIndex, value), value };
  }

  if (isBlankCellValue(value)) {
    return { node: emptyValue, value };
  }

  // Strings and numbers are the overwhelmingly common case.
  if (typeof value === 'string') {
    return { node: value, text: value, value };
  }

  // Numbers and booleans render as text; React would silently drop booleans.
  if (typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value);
    return { node: text, text, value };
  }

  // A caller may legitimately put a node straight on the row.
  if (React.isValidElement(value)) {
    return { node: value, value };
  }

  warnUnrenderableValue(column.key, value);
  const text = toDisplayText(value);

  return { node: text === '' ? emptyValue : text, text: text || undefined, value };
};

/** The `title` for a cell, or `undefined` when it should have none. */
const resolveTooltip = <TData,>(
  column: DataTableColumn<TData>,
  row: TData,
  rowIndex: number,
  truncate: boolean,
  value: unknown,
  text?: string
): string | undefined => {
  if (typeof column.tooltip === 'function') {
    return column.tooltip(row, rowIndex, value);
  }

  // Default on for truncating text columns: a clipped value the user cannot read
  // is rarely intended. `title` rather than the Tooltip component so nothing is
  // mounted per cell — a 200-cell page cannot afford 200 floating-ui instances.
  const wanted = column.tooltip ?? (truncate && !column.render);

  return wanted ? text : undefined;
};

function DataTableRowInner<TData>({
  row,
  rowIndex,
  rowKey,
  columns,
  emptyValue,
  selectionMode,
  selected,
  selectionDisabled,
  selectionLabel,
  selectionName,
  onToggleSelect,
  onRowClick,
  pinnedOffsets,
  className,
}: DataTableRowProps<TData>) {
  const isClickable = Boolean(onRowClick);

  const handleClick = () => onRowClick?.(row, rowIndex);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (!isClickable) return;

    // Ignore keys forwarded from controls inside the row (checkbox, buttons).
    if (event.target !== event.currentTarget) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  /*
   * A `change` event is a plain Event and carries no modifier keys, so shift has
   * to be read from the click that caused it. A checkbox fires `click` first and
   * only then `change` — as part of the click's activation behaviour — so this ref
   * is always populated by the time the change handler runs.
   *
   * Reset after reading, so a subsequent keyboard toggle (Space, which fires no
   * click) cannot inherit a stale modifier.
   */
  const shiftHeldRef = React.useRef(false);

  const handleSelectionClick = (event: React.MouseEvent<HTMLTableCellElement>) => {
    shiftHeldRef.current = event.shiftKey;
    // Keep selection interaction from also triggering the row's onClick.
    event.stopPropagation();
  };

  const handleSelectionChange = () => {
    const shiftKey = shiftHeldRef.current;
    shiftHeldRef.current = false;
    onToggleSelect?.(rowKey, { shiftKey });
  };

  return (
    <tr
      className={cx(
        'ds-data-table__row',
        {
          'ds-data-table__row--selected': selected,
          'ds-data-table__row--clickable': isClickable,
        },
        className
      )}
      aria-selected={selectionMode ? selected : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}>
      {selectionMode && (
        <td
          className={cx(
            'ds-data-table__cell',
            'ds-data-table__cell--selection',
            pinnedCellClassNames(SELECTION_COLUMN_KEY, pinnedOffsets)
          )}
          style={pinnedCellStyle(SELECTION_COLUMN_KEY, pinnedOffsets)}
          onClick={handleSelectionClick}>
          {selectionMode === 'single' ? (
            <Radio
              // A shared name makes the rows one radio group, so arrow keys move
              // between them the way a screen-reader user expects.
              name={selectionName}
              checked={selected}
              disabled={selectionDisabled}
              onChange={handleSelectionChange}
              aria-label={selectionLabel}
            />
          ) : (
            <Checkbox
              checked={selected}
              disabled={selectionDisabled}
              onChange={handleSelectionChange}
              aria-label={selectionLabel}
            />
          )}
        </td>
      )}

      {columns.map((column) => {
        // Custom content may contain menus or popovers, which clipping would break.
        const truncate = column.truncate ?? !column.render;
        const { node, text, value } = resolveCellContent(column, row, rowIndex, emptyValue);
        const title = resolveTooltip(column, row, rowIndex, truncate, value, text);

        return (
          <td
            key={column.key}
            className={cx(
              'ds-data-table__cell',
              `ds-data-table__cell--${column.align ?? 'left'}`,
              { 'ds-data-table__cell--truncate': truncate },
              pinnedCellClassNames(column.key, pinnedOffsets),
              column.cellClassName
            )}
            style={pinnedCellStyle(column.key, pinnedOffsets)}
            title={title}
            // An action column holds controls, so a click inside it is aimed at
            // one of them and must not also open the row.
            onClick={column.actionColumn ? (event) => event.stopPropagation() : undefined}>
            {node}
          </td>
        );
      })}
    </tr>
  );
}

/** `as typeof` keeps the generic signature, which `React.memo` alone would erase. */
const DataTableRow = React.memo(DataTableRowInner) as typeof DataTableRowInner;

export default DataTableRow;
